import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"
import { Loader2 } from "lucide-react"
import { cn } from "../../utils/utils"

// Button state system per design-system-buildmybizbrand-v1.md Section 6.
const buttonVariants = cva(
  "relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-radius-sm font-semibold transition-all disabled:pointer-events-none focus-visible:outline-none active:scale-[0.98]",
  {
    variants: {
      variant: {
        primary:
          "bg-primary text-white hover:bg-primary-dark active:shadow-none focus-visible:shadow-[0_0_0_3px_rgba(90,124,222,0.35)] disabled:bg-[#C9D3EE] disabled:text-white/70",
        secondary:
          "bg-section text-ink hover:bg-[#ECECEC] active:bg-[#E0E0E0] focus-visible:shadow-[0_0_0_3px_rgba(22,22,22,0.15)] disabled:bg-section disabled:text-ink/30",
        tertiary:
          "bg-transparent text-primary hover:text-primary-dark active:opacity-85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgba(90,124,222,0.4)] disabled:text-ink/30 disabled:pointer-events-none p-0",
        destructive:
          "bg-destructive text-white hover:bg-destructive-dark active:shadow-none focus-visible:shadow-[0_0_0_3px_rgba(214,48,63,0.35)] disabled:bg-[#F0C7CB] disabled:text-white/70",
        destructiveOutline:
          "bg-transparent text-destructive border border-destructive hover:bg-destructive-tint focus-visible:shadow-[0_0_0_3px_rgba(214,48,63,0.35)] disabled:text-ink/30 disabled:border-ink/20",
      },
      size: {
        sm: "h-9 px-4 text-[13px]",
        md: "h-11 px-5 text-[15px]",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
)

// Buttons below 44px visible height (the "sm" size) still need a 44x44
// minimum touch target — expand the hit area invisibly instead of
// changing the visible size.
const touchTargetExpander = (
  <span className="absolute -inset-y-[3px] inset-x-0" aria-hidden="true" />
)

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, loading = false, disabled, children, ...props }, ref) => {
    const isDisabled = disabled || loading

    // Radix Slot requires exactly one child to clone its props onto, so
    // asChild can't also render the touch-target expander / spinner as
    // siblings — it forwards children through untouched instead.
    if (asChild) {
      return (
        <Slot
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          disabled={isDisabled}
          aria-busy={loading || undefined}
          {...props}
        >
          {children}
        </Slot>
      )
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        {...props}
      >
        {size === "sm" && touchTargetExpander}
        {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {children}
      </button>
    )
  },
)
Button.displayName = "Button"

export { Button, buttonVariants }
